/**
 * Strategy Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between Strategy TypeScript type and Firestore data.
 *
 * Based on projectConverter pattern from firebase-core module.
 *
 * CRITICAL: Uses Client SDK signature fromFirestore(snapshot, options)
 * NOT Admin SDK signature fromFirestore(data)
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';
import type { Strategy } from '@segmently/schema-models/strategy';

export const strategyConverter: FirestoreDataConverter<Strategy> = {
  /**
   * Convert Strategy model to Firestore data
   * Called when writing to Firestore (create, update operations)
   *
   * Date Field Mapping:
   * - createdAt (Date) → createdAt (Timestamp)
   * - updatedAt (Date) → updatedAt (Timestamp)
   */
  toFirestore(strategy: Strategy): any {
    const result: any = { ...strategy };

    // Convert Date to Firestore Timestamp for createdAt
    if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = Timestamp.fromDate(new Date(result['createdAt']));
    } else if (result['createdAt'] instanceof Date) {
      result['createdAt'] = Timestamp.fromDate(result['createdAt']);
    }

    // Convert Date to Firestore Timestamp for updatedAt
    if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = Timestamp.fromDate(new Date(result['updatedAt']));
    } else if (result['updatedAt'] instanceof Date) {
      result['updatedAt'] = Timestamp.fromDate(result['updatedAt']);
    }

    // CRITICAL: Remove undefined fields (Firestore does not accept undefined values)
    // Sprint 10 fix: https://firebase.google.com/docs/reference/js/firestore_.md#setdoc
    Object.keys(result).forEach(key => {
      if (result[key] === undefined) {
        delete result[key];
      }
    });

    return result;
  },

  /**
   * Convert Firestore data to Strategy model
   * Called when reading from Firestore (get, query, snapshot operations)
   *
   * CRITICAL: Uses Client SDK signature (snapshot, options) NOT Admin SDK signature (data)
   *
   * Date Field Mapping:
   * - createdAt (Timestamp) → createdAt (ISO string)
   * - updatedAt (Timestamp) → updatedAt (ISO string)
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): Strategy {
    // Extract data from snapshot using Client SDK method
    const data = snapshot.data(options);
    const result: any = { ...data };

    // Add document ID to result
    result.id = snapshot.id;

    // Convert Firestore Timestamp to Date for createdAt
    if (result['createdAt']?.toDate) {
      result['createdAt'] = result['createdAt'].toDate();
    } else if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = new Date(result['createdAt']);
    }

    // Convert Firestore Timestamp to Date for updatedAt
    if (result['updatedAt']?.toDate) {
      result['updatedAt'] = result['updatedAt'].toDate();
    } else if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = new Date(result['updatedAt']);
    }

    return result as Strategy;
  }
};

export default strategyConverter;
